'use client';

import Link from 'next/link';
import { BadgeCheck, MessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { dancerFieldGates } from '@kit/artists';
import { BRAND } from '@kit/shared/brand';
import { Button } from '@kit/ui/button';
import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import { FollowButton } from './follow-button';
import { SaveButton } from './save-button';
import { ShareButton } from './share-button';
import type {
  Audience,
  DancerExperience,
  PublicDancerProfile,
  ViewerRole,
} from './types';

interface Props {
  dancer: PublicDancerProfile;
  audience: Audience;
  /** All experience rows for the dancer. The rail derives the
   * "Currently" block (most-recent / current-flagged engagement) and
   * the "Awards" list (entries with a `placement` value). */
  experiences?: DancerExperience[];
  /** Viewer's role on this surface — drives the gated "Casting contact"
   * block. When omitted, treated as anonymous (block hidden). */
  viewerRole?: ViewerRole;
  isFollowing?: boolean;
  isBookmarked?: boolean;
  isOwnProfile?: boolean;
}

const CONTACT_VISIBLE_ROLES: ViewerRole[] = ['owner', 'admin', 'recruiter'];

/** Cap on the awards list before "+N more" overflow. */
const AWARDS_VISIBLE_MAX = 4;

/**
 * Sticky right rail (desktop) — career-snapshot first, action second.
 *
 * Composition (top → bottom), per the 2026-05-12 redesign brief from
 * Antoine ("show current institution + role, current city, awards;
 * plus Follow + Contact-for-inquiries CTAs"):
 *
 *   1. CURRENTLY    — current engagement (rank · organization · city)
 *   2. AWARDS       — recent placements (year · placement · title)
 *   3. PRIMARY CTA  — Follow / Sign in to follow / Edit your profile
 *   4. SECONDARY CTA— Contact for inquiries (DM if authed, mailto if not)
 *   5. SAVE · SHARE — secondary actions
 *   6. CASTING      — gated contact block (owner / admin / director only)
 *
 * Removed in the redesign:
 *  - "● Active / Verified profile" status pill (every existing profile
 *    is by definition active; the badge carried no signal).
 *  - Profile-data dl rows (Styles, Years, Level, In) — they duplicated
 *    the Identity Strip directly above.
 *  - Authority-signal dl (Member since, Updated) — low priority vs
 *    career signals.
 *  - Standalone "Booking inquiries" email block — folded
 *    into the secondary CTA, which is more action-oriented.
 */
export function DancerContactRail({
  dancer,
  audience,
  experiences,
  viewerRole,
  isFollowing,
  isBookmarked,
  isOwnProfile,
}: Props) {
  const { t } = useTranslation();

  // Casting-contact gate: viewer role (registry ceiling) AND dancer's
  // own opt-in (contact_public, default false). Either gate failing
  // hides the block. AND-only semantics — the dancer's setting cannot
  // expose contact data to viewers below the registry ceiling.
  //
  // Admin (super admin moderation) bypasses the dancer's toggle.
  // Owner viewing their own public profile is a preview — the toggle
  // applies, so they see exactly what casting directors would see.
  const contactPublic = dancerFieldGates(dancer, {
    unrestricted: viewerRole === 'admin',
  }).allows('contact_public');
  // Phone is NEVER shown on the profile (2026-07-04) — casting contact
  // is email-only; phone stays a private account field.
  const showCastingContact =
    viewerRole !== undefined &&
    CONTACT_VISIBLE_ROLES.includes(viewerRole) &&
    contactPublic &&
    dancer.contact_email;

  // Career snapshot — derived from the experiences feed.
  const currentEngagement = pickCurrentEngagement(experiences);
  const awards = pickAwards(experiences);
  const visibleAwards = awards.slice(0, AWARDS_VISIBLE_MAX);
  const awardsRemainder = awards.length - visibleAwards.length;

  // Whether anything renders above the Follow CTA (verified badge,
  // "Currently" block, or awards). Drives the rail's top hairline: with
  // no snapshot content the line would float over empty space directly
  // above the CTA and read as a stray divider (2026-07-05 report).
  const showVerified = Boolean(dancer.is_verified && dancer.is_premium);
  const showCurrently = Boolean(
    currentEngagement &&
    (currentEngagement.rank_title || currentEngagement.organization_manual),
  );
  const hasSnapshotContent =
    showVerified || showCurrently || visibleAwards.length > 0;

  // Contact-for-inquiries target. Authenticated visitors get a DM
  // thread (chat); anonymous visitors get a mailto to Ballee's public
  // inbox (since they can't yet open a chat). Owner sees no contact
  // CTA — they don't message themselves.
  const subject = t('dancer:publicView.contactRail.bookingInquirySubject', {
    name:
      dancer.stage_name ?? t('dancer:publicView.contactRail.defaultDancerName'),
  });
  // Premium-dancer DMs carry `intent=booking` so the inbox can route /
  // template the message as a structured booking inquiry. Free-dancer
  // DMs stay generic. Anonymous visitors fall back to mailto.
  // (The inbox honouring `intent=booking` is a Phase 4c.2 follow-up;
  //  the URL convention is stable and frozen here.)
  const inboxBase = `/home/inbox?user=${dancer.user_id}`;
  const inboxHref = dancer.is_premium
    ? `${inboxBase}&intent=booking`
    : inboxBase;
  const inquiryHref =
    audience === 'authenticated' && !isOwnProfile
      ? inboxHref
      : `mailto:${BRAND.emails.support}?subject=${encodeURIComponent(subject)}`;
  const showInquiryCta = !isOwnProfile;

  return (
    <aside
      /* Editorial sticky rail — flat surface, hairline divider, no card
         chrome. Positioning is handled by the parent in
         `public-dancer-view.tsx`. Only visible separator on lg+ is a
         top hairline that mirrors the page's section dividers — shown
         only when the career snapshot (badge / Currently / Awards) has
         content; on sparse profiles the first item is the Follow CTA
         and the line would float orphaned. (The in-rail privacy toggle
         moved to the visibility settings page 2026-07-04 — the owner
         nudge is now the page-top private banner.) */
      className={cn(
        'border-foreground/15',
        hasSnapshotContent && 'lg:border-t lg:pt-8',
      )}
      aria-label={t('dancer:publicView.contactRail.contactAriaLabel')}
    >
      <div className="flex flex-col gap-7">
        {/* VERIFIED BADGE — gated on Dancer Premium €10/mo (Phase 4
            of the 2026-05-12 redesign). Only renders when the dancer
            has both `is_verified=true` AND an active
            `dancer-ballee-plan` subscription. The intersection keeps
            the badge meaningful: paying alone doesn't earn it, and
            verified-but-unpaid dancers don't show it. */}
        {showVerified && (
          <p className="text-foreground inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase">
            <BadgeCheck className="size-3.5" aria-hidden="true" />
            <Trans i18nKey="dancer:publicView.contactRail.verified" />
          </p>
        )}

        {/* CURRENTLY — current engagement (rank / organization).
            Renders only when there's an actual engagement to show.
            City lives in the Identity Strip above; duplicating it here
            (as the previous version did) created visual noise without
            information when an engagement was missing. */}
        {showCurrently && currentEngagement && (
          <div>
            <p className="text-muted-foreground/70 mb-3 font-mono text-[10px] tracking-[0.22em] uppercase">
              <Trans i18nKey="dancer:publicView.contactRail.currently" />
            </p>
            <div className="space-y-1 text-sm leading-snug">
              {currentEngagement.rank_title && (
                <p className="text-foreground font-medium">
                  {currentEngagement.rank_title}
                </p>
              )}
              {currentEngagement.organization_manual && (
                <p className="text-foreground/85">
                  {currentEngagement.organization_manual}
                </p>
              )}
            </div>
          </div>
        )}

        {/* AWARDS — recent placements (year · placement · title). */}
        {visibleAwards.length > 0 && (
          <div>
            <p className="text-muted-foreground/70 mb-3 inline-flex items-baseline gap-2 font-mono text-[10px] tracking-[0.22em] uppercase">
              <span>{t('dancer:publicView.contactRail.awards')}</span>
              <span className="text-muted-foreground/50">·</span>
              <span>{awards.length}</span>
            </p>
            <ul className="divide-foreground/10 divide-y text-sm">
              {visibleAwards.map((a) => {
                const year = yearOf(a.start_date);
                return (
                  <li
                    key={a.id}
                    className="grid grid-cols-[44px_1fr] items-baseline gap-3 py-2 first:pt-0 last:pb-0"
                  >
                    <span className="text-muted-foreground/70 font-mono text-[11px] tracking-[0.05em]">
                      {year ?? '—'}
                    </span>
                    <span className="text-foreground leading-snug">
                      {[a.placement, a.title]
                        .filter((v): v is string => !!v)
                        .join(' · ')}
                    </span>
                  </li>
                );
              })}
            </ul>
            {awardsRemainder > 0 && (
              <p className="text-muted-foreground/60 mt-2 font-mono text-[10px] tracking-[0.22em] uppercase">
                <Trans
                  i18nKey="dancer:publicView.contactRail.moreAwards"
                  values={{ count: awardsRemainder }}
                />
              </p>
            )}
          </div>
        )}

        {/* PRIMARY CTA — Follow context */}
        <div className="flex flex-col gap-2">
          {audience === 'public' && (
            <Button asChild variant="editorial" className="h-11 w-full">
              {/* rel=nofollow: this is a public, indexable profile. Without it,
                  Googlebot follows the link into the noindex /auth/sign-in page
                  and the index fills with `auth/sign-in?next=/dancers/<slug>`
                  URLs — one per profile — instead of the clean profile URL
                  (2026-06-08 GSC audit; critical-rules-crawl-discovery #3). */}
              <Link
                href={`/auth/sign-in?next=/dancers/${dancer.handle ?? dancer.id}`}
                rel="nofollow"
              >
                <Trans i18nKey="dancer:publicView.contactRail.signInToFollow" />
              </Link>
            </Button>
          )}

          {audience === 'authenticated' && isOwnProfile && (
            <Button asChild variant="editorial" className="h-11 w-full">
              <Link href="/home/artist/profile">
                <Trans i18nKey="dancer:publicView.contactRail.editProfile" />
              </Link>
            </Button>
          )}

          {/* Super-admin moderation: edit ANY dancer's profile in-place via
              the consolidated self-service editor (targets this dancer through
              the atomic_profile_update RPC's is_super_admin() branch). Shown
              alongside Follow, never for the owner (owner uses the block above). */}
          {audience === 'authenticated' &&
            !isOwnProfile &&
            viewerRole === 'admin' && (
              <Button asChild variant="editorial" className="h-11 w-full">
                <Link
                  href={`/home/artist/profile/edit?userId=${dancer.user_id}`}
                >
                  <Trans i18nKey="dancer:publicView.contactRail.editProfileAdmin" />
                </Link>
              </Button>
            )}

          {audience === 'authenticated' && !isOwnProfile && (
            <FollowButton
              profileUserId={dancer.user_id}
              initialIsFollowing={isFollowing ?? false}
            />
          )}

          {/* SECONDARY CTA — Contact for inquiries.
              - Authed, not own → opens DM with the dancer.
              - Anon → mailto to Ballee's public inbox.
              - Own profile → hidden. */}
          {showInquiryCta && (
            <Button asChild variant="editorial" className="h-11 w-full">
              <Link href={inquiryHref}>
                <MessageSquare className="mr-2 h-3.5 w-3.5" />
                <Trans i18nKey="dancer:publicView.contactRail.contactForInquiries" />
              </Link>
            </Button>
          )}
        </div>

        {/* Secondary actions, mono caption row.
           Save shown only for authenticated visitors who aren't the
           owner; the owner viewing their own profile bookmarking
           themselves is meaningless. */}
        <div className="text-muted-foreground/70 -mt-1 flex items-center justify-between font-mono text-[11px] tracking-[0.18em] uppercase">
          {audience === 'authenticated' && !isOwnProfile ? (
            <SaveButton
              profileUserId={dancer.user_id}
              initialIsBookmarked={isBookmarked ?? false}
            />
          ) : (
            <span aria-hidden="true" />
          )}
          <ShareButton
            name={
              dancer.stage_name ??
              dancer.handle ??
              t('dancer:publicView.hero.defaultDisplayName')
            }
          />
        </div>

        {/* CASTING CONTACT — gated to {owner, admin, recruiter} via the
            visibility registry. Coral hairline border-top is the visual
            handshake with the rail's other dividers; coral mono header
            signals "this is the privileged block." Microcopy explains
            the gate so a viewer outside those tiers who somehow sees the
            markup understands it's not a public field. */}
        {showCastingContact && (
          <div className="border-primary/60 mt-4 border-t pt-5">
            <p className="text-primary mb-3 font-mono text-[10px] tracking-[0.22em] uppercase">
              <Trans i18nKey="dancer:publicView.contactRail.castingContact" />
            </p>
            <p className="text-muted-foreground/80 mb-4 text-[11px] leading-relaxed">
              <Trans i18nKey="dancer:publicView.contactRail.castingContactDescription" />
            </p>
            <dl className="space-y-3 text-sm">
              {dancer.contact_email && (
                <div>
                  <dt className="text-muted-foreground/70 mb-0.5 font-mono text-[10px] tracking-[0.22em] uppercase">
                    <Trans i18nKey="dancer:publicView.contactRail.email" />
                  </dt>
                  <dd>
                    <a
                      href={`mailto:${dancer.contact_email}`}
                      className="text-foreground hover:text-primary break-all underline-offset-4 transition-colors hover:underline"
                    >
                      {dancer.contact_email}
                    </a>
                  </dd>
                </div>
              )}
            </dl>
          </div>
        )}
      </div>
    </aside>
  );
}

/**
 * Pick the current engagement from the experiences list.
 * Preference: explicit `current === true` and rank_title set →
 * `current === true` →
 * row with no end_date AND a rank_title set →
 * row with no end_date.
 * Returns null when nothing qualifies.
 */
function pickCurrentEngagement(
  experiences: DancerExperience[] | undefined,
): DancerExperience | null {
  if (!experiences || experiences.length === 0) return null;
  const flagged = experiences.filter((e) => e.current === true);
  const flaggedWithRank = flagged.find((e) => !!e.rank_title);
  if (flaggedWithRank) return flaggedWithRank;
  if (flagged[0]) return flagged[0];
  const openEnded = experiences.filter((e) => !e.end_date);
  const openEndedWithRank = openEnded.find((e) => !!e.rank_title);
  if (openEndedWithRank) return openEndedWithRank;
  return openEnded[0] ?? null;
}

/**
 * Pick award-shaped experiences (anything with a `placement` value),
 * sorted by start_date desc.
 */
function pickAwards(
  experiences: DancerExperience[] | undefined,
): DancerExperience[] {
  if (!experiences || experiences.length === 0) return [];
  return experiences
    .filter((e) => !!e.placement && e.placement.trim().length > 0)
    .slice()
    .sort((a, b) => (b.start_date ?? '').localeCompare(a.start_date ?? ''));
}

/**
 * Year extraction from an ISO date string. Pulled directly from the
 * leading 4 chars to avoid the `new Date(string).getFullYear()`
 * timezone shift (Critical Rule #19).
 */
function yearOf(iso: string | null): string | null {
  if (!iso) return null;
  const year = iso.slice(0, 4);
  return /^\d{4}$/.test(year) ? year : null;
}
